import type { LibraryAggregates } from "@screener/protocol";
import { SectionLabel } from "@screener/uikit/components/section-label";
import {
  DENSITIES_COUNT_HINT,
  isUnobservedOutcome,
  OUTCOME_SHARES,
  outcomeShareLabel,
  UNOBSERVED_MARK,
} from "@/lib/labels";
import { formatCount, formatPercent, formatShare } from "./format";
import { OutcomeBar, OutcomeSwatch, outcomeTextClass } from "./outcome-bar";

/**
 * Итог выборки §9, вид §116: полоса из четырёх показателей без карточек и
 * полоса долей исходов со шкалой и легендой. Всё посчитано демоном, здесь
 * только показывается; ни одна величина итога не пропала (design.md
 * stage8-library-visual, решения 1 и 2; stage9-alerts-library-redesign, Д4).
 */

/** Идентификатор заголовка раздела долей — им называется и сам раздел. */
const OUTCOME_SHARES_TITLE_ID = "library-outcome-shares-title";

interface KpiProps {
  /** Ключ величины — для тестов и снимков. */
  name: string;
  label: string;
  value: string;
  footer?: string;
}

function Kpi({ name, label, value, footer }: KpiProps) {
  return (
    <div
      data-kpi={name}
      className="flex min-w-0 flex-col gap-2 border-b border-l px-4 py-3.5 odd:border-l-0 @[48rem]/library:odd:border-l @[48rem]/library:first:border-l-0"
    >
      <SectionLabel>{label}</SectionLabel>
      <span className="text-[22px] font-semibold tracking-[-.01em] tabular-nums">{value}</span>
      {footer === undefined ? null : (
        <span className="text-xs text-muted-foreground">{footer}</span>
      )}
    </div>
  );
}

export interface LibrarySummaryProps {
  aggregates: LibraryAggregates;
}

export function LibrarySummary({ aggregates }: LibrarySummaryProps) {
  const shares = aggregates.outcome_shares;
  return (
    <div data-slot="library-summary" className="flex flex-col">
      <div data-slot="library-kpi-strip" className="grid grid-cols-2 @[48rem]/library:grid-cols-4">
        <Kpi
          name="approaches"
          label="Подходов"
          value={formatCount(aggregates.approaches)}
          footer={`Первых подходов: ${formatCount(aggregates.first_approaches)}`}
        />
        <Kpi
          name="densities"
          label="Плотностей"
          value={formatCount(aggregates.densities)}
          footer={DENSITIES_COUNT_HINT}
        />
        <Kpi
          name="mean_first_bounce_pct"
          label="Средний отскок на первом подходе"
          value={formatPercent(aggregates.mean_first_bounce_pct)}
          footer={`Средний по всем подходам: ${formatPercent(aggregates.mean_bounce_pct)}`}
        />
        <Kpi
          name="mean_discount_pct"
          label="Средний дисконт"
          value={formatPercent(aggregates.mean_discount_pct)}
        />
      </div>
      <section
        aria-labelledby={OUTCOME_SHARES_TITLE_ID}
        className="flex flex-col gap-3 border-b px-4 py-3.5"
      >
        <SectionLabel as="h2" id={OUTCOME_SHARES_TITLE_ID}>
          Доли исходов
        </SectionLabel>
        <OutcomeBar shares={shares} className="h-2" />
        <ul
          data-slot="outcome-legend"
          className="flex flex-wrap gap-x-5 gap-y-1.5 text-[13px] tabular-nums"
        >
          {OUTCOME_SHARES.map((outcome) => (
            <li key={outcome} className="inline-flex items-center gap-1.5">
              <OutcomeSwatch outcome={outcome} className="size-2 rounded-[2px]" />
              {/* Имя, пометка и доля — одна строка текста: пробелы между вложенными
                  span сохраняются и читалкой, и innerText. */}
              <span>
                <span className="font-medium">{outcomeShareLabel(outcome)}</span>{" "}
                {isUnobservedOutcome(outcome) ? (
                  <>
                    <span className="text-xs text-muted-foreground">({UNOBSERVED_MARK})</span>{" "}
                  </>
                ) : null}
                <span className={`font-semibold ${outcomeTextClass(outcome) ?? ""}`}>
                  {formatShare(shares[outcome])}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
